/**
 * How a phone number becomes something a messaging provider can dial.
 *
 * `volunteers.phone` is stored exactly as the person typed it (§3.12), so the
 * same mobile legitimately appears as `+91 98450 12345`, `098450 12345` or
 * `98450 12345`, and staff entry additionally allows landlines and international
 * numbers. That is right for a record a human reads and wrong for an API call,
 * which needs one canonical form.
 *
 * Derived here rather than in n8n for the same reason `duration_display` is: no
 * workflow should have to know how an Indian mobile number is written, and three
 * workflows each parsing it their own way is three chances to disagree.
 *
 * **Nothing here rewrites what is stored.** These functions read a number and
 * report what it is; the stored value is left as the person typed it.
 */
/**
 * The digits of a phone number, with an Indian country code or a trunk `0`
 * stripped, or null if it is not a 10-digit national number.
 *
 * `+91 98450 12345`, `098450 12345` and `98450 12345` are the same mobile number
 * written three ways; all three reduce to the ten digits that identify it. What
 * this rejects is a number that is not ten digits once those are removed.
 */
export function nationalTenDigits(value) {
    let digits = value.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91'))
        digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith('0'))
        digits = digits.slice(1);
    return digits.length === 10 ? digits : null;
}
/**
 * The same number in E.164 — `+919845012345` — or null when it is not a
 * reachable Indian mobile.
 *
 * **Stricter than `nationalTenDigits`, deliberately.** That function answers
 * "is this ten digits?", which is the right question for the registration form:
 * §3 wants a willing volunteer accepted, and staff may record a landline on a
 * profile. This one answers "can a message be sent here?", and the cost of a
 * wrong yes is different — a message addressed to a number nobody reads, once
 * per session, reported as delivered.
 *
 * So this additionally requires the mobile range: every Indian mobile begins
 * 6, 7, 8 or 9, which rejects a landline written with its trunk zero and STD
 * code — `080 2345 6789` … no. Two STD codes, **079** (Ahmedabad) and **080**
 * (Bengaluru), collapse into the mobile range and cannot be told apart from a
 * mobile by any rule that only looks at the digits. Those get through, the send
 * fails at the provider, and the failure is visible in the n8n execution list.
 * That is the honest limit of this check.
 *
 * **Null rather than a guess.** A landline, a short number or an international
 * number all return null, and a null is what makes a workflow skip the channel
 * and say why. Inventing a country code for a number that might not be Indian
 * would turn a visible skip into a message delivered to a stranger.
 */
export function toE164(value) {
    if (value === null || value === undefined)
        return null;
    const national = nationalTenDigits(value);
    if (national === null)
        return null;
    return /^[6-9]/.test(national) ? `+91${national}` : null;
}
//# sourceMappingURL=phone.js.map